import { useState, useEffect, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import BottomNav from './BottomNav';

export default function AppLayout({ children }: { children: ReactNode }) {
  const { isDark } = useTheme();
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/login';
  const isRegisterPage = location.pathname === '/register';
  const [sidebarCollapsed] = useState(false);

  // Public pages (no auth needed)
  const isPublicPage = isLoginPage || isRegisterPage;

  // Redirect unauthenticated users to login (useEffect to avoid render-time navigation)
  useEffect(() => {
    if (!isLoading && !user && !isPublicPage) {
      navigate('/login', { replace: true });
    }
  }, [isLoading, user, isPublicPage, navigate]);

  // Redirect authenticated users away from login/register
  useEffect(() => {
    if (!isLoading && user && isPublicPage) {
      navigate('/', { replace: true });
    }
  }, [isLoading, user, isPublicPage, navigate]);

  if (isPublicPage) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        {children}
      </div>
    );
  }

  // While loading auth state, show a loading screen instead of layout
  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            Yükleniyor...
          </p>
        </div>
      </div>
    );
  }

  // If not authenticated and not on public page, don't render layout (redirect will happen)
  if (!user) {
    return null;
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Topbar */}
      <div className="hidden lg:block">
        <Topbar />
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 z-30 flex items-center justify-between px-4 bg-white/80 dark:bg-dark-card/80 backdrop-blur-glass border-b border-light-border dark:border-dark-border">
        <div className="flex items-center gap-2">
          <img
            src="https://public.readdy.ai/ai/img_res/8d501578-6f11-4960-92e0-74720553c4c2.png"
            alt="Logo"
            className="w-7 h-7 object-contain"
          />
          <span className={`font-bold text-base ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            EmlakPro
          </span>
        </div>
        <button
          onClick={() => {}}
          className={`w-8 h-8 flex items-center justify-center rounded-lg ${
            isDark ? 'text-dark-muted' : 'text-light-muted'
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-notification-3-line text-lg" />
          </div>
        </button>
      </div>

      {/* Main Content */}
      <main
        className={`transition-all duration-300 pt-14 lg:pt-16 pb-16 lg:pb-0 min-h-screen ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        }`}
      >
        <div className="p-4 md:p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <BottomNav />
    </div>
  );
}